import type {
  AccommodationTip,
  AccommodationTipsApiResponse,
  CollectionTeaser,
  GA4FilterCollectionEvent,
  SearchAPIFilter,
  SearchAPIRawResponse,
  SearchFilters,
  SearchLocation,
  SearchResponse,
  SearchResult,
} from './types'
import { decorateUrl } from '@/scopes/search/search/infrastructure/live-search/api/liveSearchApiClient'
import { createFilterMapper, type FilterMapper } from './filterMapper'

interface Dependencies {
  filterMapper: FilterMapper
}

export interface SearchResponseMapper {
  map: (searchResponse: SearchAPIRawResponse) => SearchResponse
  mapAIFilters: (getAIFiltersRawResponse: Record<string, string[] | null>) => Record<string, string[] | null>
}

export function createSearchResponseMapper(dependencies: Partial<Dependencies> = {}): SearchResponseMapper {
  const { filterMapper = createFilterMapper() } = dependencies as Dependencies

  function map(searchResponse: SearchAPIRawResponse) {
    return {
      sorting: getMappedSorting(searchResponse),
      pagination: getMappedPagination(searchResponse),
      totalResults: getMappedTotalResults(searchResponse),
      filters: filterMapper.map(searchResponse.filters),
      results: getMappedResults(searchResponse.results),
      ...(searchResponse.collectionTeaser && {
        filteredCollection: getMappedFilteredCollection(searchResponse.collectionTeaser),
      }),
      accommodationTips: getMappedAccommodationTips(searchResponse.accoTips),
      ga4FilterCollection: getMappedGA4FilterCollection(searchResponse),
      ga4Impressions: getMappedGA4Impressions(searchResponse),
    }
  }

  function getMappedResults(results: SearchResult[]) {
    results.forEach((result) => {
      result.urlWithFilters = decorateUrl(result.urlWithFilters, 'originList=search-result')
      if (result.campaign) {
        result.campaign.icon = mapIcon(result.campaign?.icon)
      }
    })
    return results.sort(byIsHighlighted)
  }

  function byIsHighlighted(r1: SearchResult, r2: SearchResult) {
    if (r1.isHighlighted && !r2.isHighlighted)
      return -1
    if (!r1.isHighlighted && r2.isHighlighted)
      return 1

    return 0
  }

  function getMappedAccommodationTips(tips: AccommodationTipsApiResponse[] | undefined) {
    if (tips === undefined) {
      return []
    }

    return tips.map(
      (item): AccommodationTip => ({
        arrivalDate: item.arrivalDateDetail,
        departureDate: item.departureDateDetail,
        departureDateRaw: item.price?.acmInformation?.departureDateRaw,
        durationRaw: item.price?.acmInformation?.durationRaw,
        id: item.id,
        images: item.images,
        locations: item.locations,
        mealPlan: item.featuredFilters?.mealplan,
        name: item.name,
        occupancy: item.featuredFilters?.occupancy,
        price: item.price?.averagePrice,
        rating: item.rating,
        transportType: item.transportType,
        url: item.urlWithFilters,
        userRating: item.userRating,
        userRatingText: item.userRatingText,
        outboundDepartureAirportName: item.outboundDepartureAirportName,
      }),
    )
  }

  function getMappedFilteredCollection(collectionTeaser: CollectionTeaser) {
    const { collection, text: description, image, title, usps } = collectionTeaser

    return {
      collection,
      description,
      image,
      title,
      usps,
    }
  }

  function getMappedSorting(searchResponse: SearchAPIRawResponse) {
    const { property: by } = searchResponse.sortInfo
    return { by }
  }

  function getMappedPagination(searchResponse: SearchAPIRawResponse) {
    const { offset, limit } = searchResponse.pagination
    return { offset, limit }
  }

  function getMappedTotalResults(searchResponse: SearchAPIRawResponse) {
    const { totalResults } = searchResponse.pagination
    return totalResults
  }

  function getMappedGA4FilterCollection(searchResponse: SearchAPIRawResponse) {
    const { filters } = searchResponse
    return mapFiltersFromAPIResponseToGA4FilterCollection(filters)
  }

  function getMappedGA4Impressions(searchResponse: SearchAPIRawResponse) {
    const { results } = searchResponse
    return mapSearchResultsToImpressions(results)
  }

  function transformKeysToUpperCase(obj: Record<string, string[] | null>): Record<string, string[] | null> {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key.charAt(0).toUpperCase() + key.slice(1), value]),
    )
  }

  function mapAIFilters(getAIFiltersRawResponse: Record<string, string[] | null>) {
    return transformKeysToUpperCase(getAIFiltersRawResponse)
  }

  return { map, mapAIFilters }
}

function mapFiltersFromAPIResponseToGA4FilterCollection(filters: SearchAPIRawResponse['filters']) {
  const filterCollection: GA4FilterCollectionEvent = {}
  const filterTypeToPropertyMap: Partial<Record<keyof SearchFilters, keyof GA4FilterCollectionEvent>> = {
    AccommodationType: 'accoType',
    ArrivalAirport: 'arrival airports',
    Campaigns: 'campaigns',
    ChildrenFacilities: 'children facility',
    Collection: 'labels',
    City: 'city',
    Country: 'country',
    CustomersRating: 'ratings',
    DepartureAirport: 'airports',
    DepartureDate: 'departureDate',
    Discount: 'discount',
    Duration: 'duration',
    TransportType: 'transport',
    Facilities: 'general facility',
    InternetFacilities: 'internet facility',
    Lastminute: 'departureWeeks',
    Mealplan: 'boardType',
    MetersToBeach: 'distance from sea',
    MetersToCenter: 'distance from city center',
    PricePerPerson: 'price',
    Participants: 'partyComposition',
    Region: 'region',
    RoomCount: 'number of rooms',
    Stars: 'stars',
    Themes: 'themes',
  }

  filters.forEach((filter) => {
    const selectedValues = filter.values.filter(value => value.isSelected).map(value => value.value.toLowerCase())
    const property = filterTypeToPropertyMap[filter.type]

    if (selectedValues.length === 0 || !property) {
      return
    }

    const value = mapFilterToGA4Value(filter, selectedValues)
    filterCollection[property] = value
  })

  return filterCollection
}

function mapFilterToGA4Value(filter: SearchAPIFilter, selectedValues: string[]) {
  switch (filter.type) {
    case 'City':
    case 'Country':
    case 'Region':
      return mapCaptionOfFilterToGA4Value(filter)
    case 'DepartureDate':
      return mapDepartureDateFilterToGA4Value(selectedValues)
    case 'PricePerPerson':
      return mapPricePerPersonFilterToGA4Value(filter)
    case 'Participants':
      return mapParticipantsFilterToGA4Value(filter)
    default:
      return mapDefaultFilterToGa4Value(selectedValues)
  }
}

function mapDefaultFilterToGa4Value(selectedValues: string[]) {
  return selectedValues.join(',')
}

function mapCaptionOfFilterToGA4Value(filters: SearchAPIFilter) {
  const selectedCaptions = filters.values
    .filter(filter => filter.isSelected)
    .map(filter => filter.caption.toLowerCase())

  return selectedCaptions.join(',')
}

function mapDepartureDateFilterToGA4Value(selectedValues: string[]) {
  if (selectedValues.length > 1) {
    return `${selectedValues.shift()} ${selectedValues.pop()}`
  }
  return selectedValues.join(',')
}

function mapPricePerPersonFilterToGA4Value(filter: SearchAPIFilter) {
  const selectedCaptions = filter.values
    .filter(value => value.isSelected)
    .map(value => value.caption.toLowerCase())
  const firstValue = selectedCaptions.shift()?.replace(' ', '')
  const lastValue = selectedCaptions.pop()?.replace(' ', '')

  return `${firstValue} ${lastValue}`
}

function mapParticipantsFilterToGA4Value(filter: SearchAPIFilter) {
  const selectedMetaData = filter.values
    .filter(value => value.isSelected && value.metadata)
    .map(value => value.metadata)
  const adultsAmount = selectedMetaData.filter(metadata => metadata?.includes('Type:Adult')).length
  const childrenAmount = selectedMetaData.filter(metadata => metadata?.includes('Type:Child')).length
  const babyAmount = selectedMetaData.filter(metadata => metadata?.includes('Type:Baby')).length

  return `a${adultsAmount},c${childrenAmount},b${babyAmount}`
}

function mapSearchResultsToImpressions(results: SearchResult[]) {
  return results.map((result, index) => {
    return {
      list: 'search result',
      id: result.id ? result.id : '',
      name: result.name ? result.name : '',
      duration: result.featuredFilters?.duration ? result.featuredFilters.duration : '',
      totalGuests: calculateOccupancy(result.occupancyInfo),
      departureDate: result.departureDateDetail ? result.departureDateDetail : '',
      category: formatLocations(result.locations),
      position: index + 1,
      accoStars: result.rating ? result.rating : '',
      badges: result.collection ? result.collection : '',
      rating: result.userRating ? result.userRating : '',
      entryPrice: result.price?.averagePrice ? result.price.averagePrice : 0,
      campaignId: result.price?.yieldCampaignId ? result.price.yieldCampaignId : '',
      campaignName: result.price?.yieldCampaignName ? result.price.yieldCampaignName : '',
      cityId: result.locations.find(l => l.type === 'city')?.deskId || '',
      countryId: result.locations.find(l => l.type === 'country')?.deskId || '',
      regionId: result.locations.find(l => l.type === 'region')?.deskId || '',
    }
  })
}

export function formatLocations(locations: SearchLocation[]) {
  return locations
    .reduce<string[]>((acc, location) => {
      if (location.type === 'country' || location.type === 'region' || location.type === 'city') {
        if (location.name) {
          acc.push(location.name)
        }
      }
      return acc
    }, [])
    .join(' > ')
}

function calculateOccupancy(occupancyInfo?: { adultsCount: number, babiesCount: number, childrenCount: number }) {
  let numberGuests = 0

  if (occupancyInfo) {
    const adults = occupancyInfo.adultsCount ? occupancyInfo.adultsCount : 0
    const babies = occupancyInfo.babiesCount ? occupancyInfo.babiesCount : 0
    const children = occupancyInfo.childrenCount ? occupancyInfo.childrenCount : 0

    numberGuests = adults + babies + children
  }

  return numberGuests
}

function mapIcon(icon: string) {
  if (!icon) {
    return ''
  }

  return (
    `Icon${
      icon
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('')}`
  )
}
